import React, { useRef, useMemo } from 'react';
import styled from 'styled-components';
import { Typography, Box } from '@mui/material';
import { Portal } from 'react-portal';
import { FlexSpace, StatusChip } from '../../../components';
import { formatDuration, numberWithCommas } from '../../../utils/common';
import { localizeDate } from '../../../utils/datetime';
import { fontSize, fontWeight, radius, space } from '../../../style/utils';

const TooltipWrapper = styled.div`
  background: ${({ theme }) => theme.palette.surface.base};
  border: 1px solid ${({ theme }) => theme.palette.border.muted};
  border-radius: ${radius('sm')};
  padding: ${space('xxl')};
  color: ${({ theme }) => theme.palette.content.primary};

  & p,
  & span {
    font-size: ${fontSize('base')};
    font-weight: ${fontWeight('regular')};
  }
`;

const TooltipItem = styled(FlexSpace)`
  gap: ${space('smMinus')};
  &:not(:last-of-type) {
    margin-bottom: ${space('sm')};
  }
`;

const AbsoluteWrapper = styled.div.attrs(
  (props: { left: number; top: number }) => ({
    style: {
      left: props.left,
      top: props.top,
    },
  }),
)<{ left: number; top: number }>`
  position: absolute;
`;

const getPosition = (ref: any) => {
  if (!ref?.current) return null;
  const clientRect = ref?.current?.getBoundingClientRect();
  return {
    left: clientRect.left + document.body.scrollLeft,
    top: clientRect.top + document.body.scrollTop,
  };
};

const Tooltip = ({ payload }: any) => {
  const data = useMemo(() => payload?.[0]?.payload, [payload]);

  const date = useMemo(() => {
    const parsed = localizeDate(data.time_utc);
    return parsed.isValid() && parsed.format('L LTS');
  }, [data.time_utc]);

  const execTimeStr = useMemo(() => {
    if (data.status === 'error') return '0 Secs';
    return formatDuration(data.execution_time);
  }, [data]);

  const color = useMemo(
    () =>
      data.status === 'fail' || data.status === 'error'
        ? 'status.error.bright'
        : 'status.warning.main',
    [data.status],
  );

  return (
    <TooltipWrapper>
      <StatusChip status={data.status} sx={{ mb: 2 }} size="small" />

      <Typography sx={{ mb: 1 }}>{date}</Typography>

      {data.affected_rows > 0 && (
        <TooltipItem>
          <Typography color={color}>Failed results:</Typography>
          <Typography color={color}>
            {numberWithCommas(data.affected_rows, 0)}
          </Typography>
        </TooltipItem>
      )}
      <TooltipItem>
        <Typography sx={{ color: 'content.secondary', width: '110px' }}>
          Execution Time:
        </Typography>
        <Typography>{execTimeStr}</Typography>
      </TooltipItem>
    </TooltipWrapper>
  );
};

const RunsChartTooltip = ({ active, payload, floatTooltip }: any) => {
  const tooltipRef = useRef<any>();

  if (!(active && payload?.[0]?.payload)) {
    return null;
  }

  if (floatTooltip) {
    return (
      <>
        <Box ref={tooltipRef} sx={{ visibility: 'hidden' }}>
          <Tooltip payload={payload} />
        </Box>

        <Portal>
          <AbsoluteWrapper
            left={getPosition(tooltipRef)?.left}
            top={getPosition(tooltipRef)?.top}
          >
            <Tooltip payload={payload} />
          </AbsoluteWrapper>
        </Portal>
      </>
    );
  }

  return <Tooltip payload={payload} />;
};

export default RunsChartTooltip;
